// HR-004 follow-up — Biometric Devices admin page.
//
// Two cards stacked vertically:
//   1. Registered Devices (eSSL AIFace Eris, etc.) — CRUD
//   2. Employee Mapping — CSV upload to bulk-map emp_code → biometric_id

import { useMemo, useState } from 'react'
import {
  App, Button, Card, Form, Input, Modal, Popconfirm, Select, Space, Switch,
  Table, Tag, Tooltip, Typography, Upload, Alert,
} from 'antd'
import {
  CloudServerOutlined, DownloadOutlined, EditOutlined, PlusOutlined,
  CloudDownloadOutlined, PoweroffOutlined, CloseCircleOutlined,
  ThunderboltOutlined, UploadOutlined, UserSwitchOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UploadProps } from 'antd'
import dayjs from 'dayjs'

import {
  biometricApi, type BiometricDevice, type BiometricDeviceCreate,
  type BiometricDevicePatch, type BulkMapResult, type EmployeeMapping,
} from '@/features/hr/biometricApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text, Title } = Typography


export default function BiometricDevicesPage() {
  return (
    <div>
      <PageHeader
        title="Biometric Devices"
        subtitle="Register your eSSL / ZKTeco devices and map employee biometric IDs. Punches arrive at /iclock/cdata and flow into Daily Attendance + Reconciliation."
      />
      <DevicesCard />
      <MappingCard />
    </div>
  )
}


// ─── Devices CRUD ─────────────────────────────────────────────────────

function DevicesCard() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const { plantId } = usePlantId()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<BiometricDevice | null>(null)

  const { data: devices, isLoading } = useQuery({
    queryKey: ['biometric-devices', plantId],
    queryFn: () => biometricApi.listDevices({ plant_id: plantId ?? undefined }),
    enabled: !!plantId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['biometric-devices'] })

  const createMut = useMutation({
    mutationFn: (body: BiometricDeviceCreate) => biometricApi.createDevice(body),
    onSuccess: () => { message.success('Device registered'); invalidate(); setCreateOpen(false) },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: BiometricDevicePatch }) =>
      biometricApi.patchDevice(id, body),
    onSuccess: () => { message.success('Updated'); invalidate(); setEditing(null) },
  })

  const forceResendMut = useMutation({
    mutationFn: (id: string) => biometricApi.forceResend(id),
    onSuccess: (res) => {
      message.success({
        content: (
          <span>
            Re-upload flag set for <b>{res.device_name}</b>. Only takes
            effect on next handshake (reboot / network reconnect). If the
            device is online and not pushing, use <b>Reboot</b> instead.
          </span>
        ),
        duration: 8,
      })
      invalidate()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Force-resend failed')
    },
  })

  const rebootMut = useMutation({
    mutationFn: (id: string) => biometricApi.rebootDevice(id, true),
    onSuccess: (res) => {
      message.success({
        content: (
          <span>
            Reboot queued for <b>{res.device_name}</b>. Device will reboot
            within ~30 sec, then re-push its full buffer on reboot
            handshake. Watch Punching Logs over the next 2–5 minutes.
          </span>
        ),
        duration: 10,
      })
      invalidate()
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Reboot queue failed')
    },
  })

  const cancelCmdMut = useMutation({
    mutationFn: (id: string) => biometricApi.cancelCommand(id),
    onSuccess: () => { message.success('Pending command cleared'); invalidate() },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Cancel failed')
    },
  })

  const columns = [
    {
      title: 'Device', key: 'name',
      render: (_: unknown, r: BiometricDevice) => (
        <Space size={4} direction="vertical">
          <Space size={6}>
            <CloudServerOutlined style={{ color: '#1677ff' }} />
            <Text strong>{r.device_name}</Text>
            {r.device_model && <Tag>{r.device_model}</Tag>}
          </Space>
          {r.location && (
            <Text type="secondary" style={{ fontSize: 11 }}>{r.location}</Text>
          )}
        </Space>
      ),
    },
    {
      title: 'Serial Number', dataIndex: 'serial_number', key: 'sn',
      render: (s: string) => <Text code>{s}</Text>,
    },
    { title: 'IP', dataIndex: 'ip_address', key: 'ip',
      render: (v: string | null) => v ?? '—' },
    {
      title: 'Direction', dataIndex: 'default_direction', key: 'dir',
      render: (v: string) => (
        <Tag color={v === 'IN' ? 'green' : v === 'OUT' ? 'red' : 'default'}>
          {v}
        </Tag>
      ),
    },
    {
      title: 'Last seen', dataIndex: 'last_seen_at', key: 'seen',
      render: (v: string | null) => {
        if (!v) return <Text type="secondary" style={{ fontSize: 11 }}>never</Text>
        const ago = dayjs(v).fromNow ? dayjs(v).fromNow() : dayjs(v).format('DD MMM HH:mm')
        const recent = dayjs().diff(dayjs(v), 'minute') < 5
        return (
          <Space size={4}>
            <Tag color={recent ? 'green' : 'default'} style={{ margin: 0 }}>
              {recent ? 'LIVE' : 'STALE'}
            </Tag>
            <Text type="secondary" style={{ fontSize: 11 }}>{ago}</Text>
          </Space>
        )
      },
    },
    {
      title: 'Status', dataIndex: 'is_active', key: 'st',
      render: (b: boolean) => (
        <Tag color={b ? 'success' : 'default'}>
          {b ? 'ACTIVE' : 'INACTIVE'}
        </Tag>
      ),
    },
    {
      title: 'Pending Cmd', key: 'pendingCmd',
      width: 130,
      render: (_: unknown, r: BiometricDevice) => {
        if (r.pending_command) {
          return (
            <Space size={4} direction="vertical" align="start">
              <Tag color="processing" icon={<ThunderboltOutlined />}>
                {r.pending_command}
              </Tag>
              {r.pending_command_set_at && (
                <Text type="secondary" style={{ fontSize: 10 }}>
                  queued {dayjs(r.pending_command_set_at).fromNow ? dayjs(r.pending_command_set_at).fromNow() : ''}
                </Text>
              )}
            </Space>
          )
        }
        if (r.force_resend_until && dayjs(r.force_resend_until).isAfter(dayjs())) {
          return <Tag color="gold">RESEND ARMED</Tag>
        }
        return <Text type="secondary" style={{ fontSize: 11 }}>—</Text>
      },
    },
    ...(canEdit ? [{
      title: 'Actions', key: 'actions',
      width: 200,
      render: (_: unknown, r: BiometricDevice) => (
        <Space size={4}>
          <Popconfirm
            title="Reboot device + force re-upload?"
            description={
              <div style={{ maxWidth: 320, fontSize: 12 }}>
                <p style={{ margin: '0 0 6px 0' }}>
                  Queues a <b>REBOOT</b> command via eSSL ADMS. On its
                  next 15–30 sec poll, <b>{r.device_name}</b> will reboot,
                  re-handshake, and re-push its full flash buffer
                  (last 3–30 days of punches).
                </p>
                <p style={{ margin: 0, color: '#888' }}>
                  This is the reliable Force Re-upload. Use the cloud-down
                  button only if you don't want the device to reboot.
                </p>
              </div>
            }
            okText="Reboot now"
            cancelText="Cancel"
            okButtonProps={{ danger: true, loading: rebootMut.isPending }}
            onConfirm={() => rebootMut.mutate(r.id)}
            disabled={!r.is_active}
          >
            <Tooltip title={r.is_active ? 'Reboot + force re-upload (recommended)' : 'Device is inactive'}>
              <Button
                size="small"
                type="text"
                danger
                icon={<PoweroffOutlined />}
                disabled={!r.is_active}
                loading={rebootMut.isPending && rebootMut.variables === r.id}
              />
            </Tooltip>
          </Popconfirm>

          <Popconfirm
            title="Force re-upload (no reboot)?"
            description={
              <div style={{ maxWidth: 280, fontSize: 12 }}>
                <p style={{ margin: '0 0 6px 0' }}>
                  Sets <code>force_resend_until = +10 min</code> on{' '}
                  <b>{r.device_name}</b>. Effective only on the NEXT
                  natural handshake (boot or network reconnect).
                </p>
                <p style={{ margin: 0, color: '#888' }}>
                  If the device is happily polling, this flag will sit
                  unused. Use Reboot instead for an immediate effect.
                </p>
              </div>
            }
            okText="Set flag"
            cancelText="Cancel"
            okButtonProps={{ loading: forceResendMut.isPending }}
            onConfirm={() => forceResendMut.mutate(r.id)}
            disabled={!r.is_active}
          >
            <Tooltip title="Set resend flag (waits for handshake)">
              <Button
                size="small"
                type="text"
                icon={<CloudDownloadOutlined />}
                disabled={!r.is_active}
                loading={forceResendMut.isPending && forceResendMut.variables === r.id}
              />
            </Tooltip>
          </Popconfirm>

          {(r.pending_command || (r.force_resend_until &&
            dayjs(r.force_resend_until).isAfter(dayjs()))) && (
            <Popconfirm
              title="Cancel pending command + clear resend flag?"
              onConfirm={() => cancelCmdMut.mutate(r.id)}
              okText="Cancel command"
            >
              <Tooltip title="Cancel pending command / resend flag">
                <Button
                  size="small"
                  type="text"
                  icon={<CloseCircleOutlined />}
                  loading={cancelCmdMut.isPending && cancelCmdMut.variables === r.id}
                />
              </Tooltip>
            </Popconfirm>
          )}

          <Button size="small" type="text" icon={<EditOutlined />}
            onClick={() => setEditing(r)} />
        </Space>
      ),
    }] : []),
  ]

  return (
    <Card
      style={{ marginTop: 16 }}
      title={
        <Space>
          <CloudServerOutlined />
          <span>Registered Devices</span>
        </Space>
      }
      extra={canEdit && (
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          Register Device
        </Button>
      )}
    >
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="One row per physical device. Register every device that will push punches to this system."
        description={
          <span style={{ fontSize: 12 }}>
            Configure each device's ADMS / Cloud Server Setting to point at{' '}
            <Text code>https://erp.unitechspin.com/api/v1/iclock/cdata</Text>{' '}
            with port <b>443</b> and HTTPS <b>ON</b>. The serial number on
            the device's "System Info" screen goes into the form below.
          </span>
        }
      />
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns as never}
        dataSource={devices ?? []}
        pagination={false}
        size="small"
      />
      <DeviceCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(body) => createMut.mutate(body)}
        submitting={createMut.isPending}
      />
      <DeviceEditModal
        device={editing}
        onClose={() => setEditing(null)}
        onSubmit={(id, body) => patchMut.mutate({ id, body })}
        submitting={patchMut.isPending}
      />
    </Card>
  )
}


function DeviceCreateModal({ open, onClose, onSubmit, submitting }: {
  open: boolean
  onClose: () => void
  onSubmit: (body: BiometricDeviceCreate) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const { plantId } = usePlantId()
  return (
    <Modal
      open={open} onCancel={onClose}
      title="Register Biometric Device" width={520}
      okText="Register" confirmLoading={submitting}
      onOk={() => form.validateFields().then((v) => {
        if (!plantId) return
        onSubmit({
          plant_id: plantId,
          serial_number: v.serial_number,
          device_name: v.device_name,
          device_model: v.device_model || null,
          location: v.location || null,
          ip_address: v.ip_address || null,
          default_direction: v.default_direction || 'UNKNOWN',
          remarks: v.remarks || null,
        })
      })}
    >
      <Form form={form} layout="vertical" initialValues={{
        default_direction: 'UNKNOWN', device_model: 'AIFace Eris',
      }}>
        <Form.Item name="serial_number" label="Serial Number"
          rules={[{ required: true }]}
          tooltip="Find in: device Menu → System Info → Device Info → Serial No.">
          <Input placeholder="e.g. AVR9233700123" />
        </Form.Item>
        <Form.Item name="device_name" label="Device Name"
          rules={[{ required: true }]}>
          <Input placeholder="e.g. Main Gate" />
        </Form.Item>
        <Form.Item name="device_model" label="Model">
          <Input placeholder="e.g. AIFace Eris" />
        </Form.Item>
        <Form.Item name="location" label="Location">
          <Input placeholder="e.g. Plant entry / Ring Frame floor" />
        </Form.Item>
        <Form.Item name="ip_address" label="IP Address (optional, for reference)">
          <Input placeholder="e.g. 192.168.1.100" />
        </Form.Item>
        <Form.Item name="default_direction" label="Default Direction"
          tooltip="If the device's status field is unset/unknown, what direction should we tag punches with?">
          <Select options={[
            { label: 'UNKNOWN (use device status field)', value: 'UNKNOWN' },
            { label: 'IN (e.g. entry gate only)', value: 'IN' },
            { label: 'OUT (e.g. exit gate only)', value: 'OUT' },
          ]} />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}


function DeviceEditModal({ device, onClose, onSubmit, submitting }: {
  device: BiometricDevice | null
  onClose: () => void
  onSubmit: (id: string, body: BiometricDevicePatch) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  return (
    <Modal
      open={!!device} onCancel={onClose}
      title={device ? `Edit ${device.device_name}` : 'Edit'}
      okText="Save" confirmLoading={submitting} width={520}
      onOk={() => {
        if (!device) return
        form.validateFields().then((v) => {
          onSubmit(device.id, {
            device_name: v.device_name,
            device_model: v.device_model || null,
            location: v.location || null,
            ip_address: v.ip_address || null,
            default_direction: v.default_direction,
            is_active: v.is_active,
            remarks: v.remarks || null,
          })
        })
      }}
      destroyOnClose
    >
      {device && (
        <Form form={form} layout="vertical" initialValues={{
          device_name: device.device_name,
          device_model: device.device_model ?? '',
          location: device.location ?? '',
          ip_address: device.ip_address ?? '',
          default_direction: device.default_direction,
          is_active: device.is_active,
          remarks: device.remarks ?? '',
        }}>
          <Form.Item label="Serial Number (immutable)">
            <Input value={device.serial_number} disabled />
          </Form.Item>
          <Form.Item name="device_name" label="Device Name">
            <Input />
          </Form.Item>
          <Form.Item name="device_model" label="Model">
            <Input />
          </Form.Item>
          <Form.Item name="location" label="Location">
            <Input />
          </Form.Item>
          <Form.Item name="ip_address" label="IP Address">
            <Input />
          </Form.Item>
          <Form.Item name="default_direction" label="Default Direction">
            <Select options={[
              { label: 'UNKNOWN', value: 'UNKNOWN' },
              { label: 'IN', value: 'IN' },
              { label: 'OUT', value: 'OUT' },
            ]} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}


// ─── Employee Mapping ─────────────────────────────────────────────────

function MappingCard() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const { plantId } = usePlantId()
  const [uploadResult, setUploadResult] = useState<BulkMapResult | null>(null)
  const [editing, setEditing] = useState<EmployeeMapping | null>(null)
  const [quickOpen, setQuickOpen] = useState(false)
  const [search, setSearch] = useState('')

  const { data: mappings, isLoading } = useQuery({
    queryKey: ['biometric-mappings', plantId],
    queryFn: () => biometricApi.listMappings({ plant_id: plantId ?? undefined }),
    enabled: !!plantId,
  })

  const invalidateMappings = () =>
    qc.invalidateQueries({ queryKey: ['biometric-mappings'] })

  // Single-employee mapping mutation — used by inline edit + Quick Map button.
  const singleMut = useMutation({
    mutationFn: ({ employee_id, biometric_id }:
      { employee_id: string; biometric_id: string }) =>
      biometricApi.mapEmployee(employee_id, biometric_id),
    onSuccess: () => {
      message.success('Mapping saved')
      invalidateMappings()
      setEditing(null)
      setQuickOpen(false)
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Save failed')
    },
  })

  const filtered = useMemo(() => {
    const list = mappings ?? []
    if (!search.trim()) return list
    const q = search.toLowerCase()
    return list.filter((e) =>
      e.emp_code.toLowerCase().includes(q) ||
      e.emp_name.toLowerCase().includes(q) ||
      (e.biometric_id ?? '').toLowerCase().includes(q),
    )
  }, [mappings, search])

  const stats = useMemo(() => {
    const list = mappings ?? []
    return {
      total: list.length,
      mapped: list.filter((e) => !!e.biometric_id).length,
      unmapped: list.filter((e) => !e.biometric_id).length,
    }
  }, [mappings])

  const bulkMut = useMutation({
    mutationFn: (rows: Array<{ emp_code: string; biometric_id: string }>) =>
      biometricApi.bulkMap(rows, plantId ?? undefined),
    onSuccess: (result) => {
      setUploadResult(result)
      if (result.ok) {
        message.success(`${result.updated} mapped · ${result.skipped} unchanged`)
        qc.invalidateQueries({ queryKey: ['biometric-mappings'] })
      } else {
        message.error(result.error ?? `${result.errors} error(s) — see report below`)
      }
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { detail?: string } } }
      message.error(err?.response?.data?.detail ?? 'Upload failed')
    },
  })

  const downloadTemplate = () => {
    const rows = mappings ?? []
    const lines = [
      'emp_code,emp_name,biometric_id',
      ...rows.map((e) =>
        `${csvCell(e.emp_code)},${csvCell(e.emp_name)},${csvCell(e.biometric_id ?? '')}`
      ),
    ]
    const csv = lines.join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `biometric-mapping-template-${dayjs().format('YYYY-MM-DD')}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const uploadProps: UploadProps = {
    accept: '.csv',
    maxCount: 1,
    showUploadList: false,
    beforeUpload: async (file) => {
      try {
        const text = await file.text()
        const rows = parseCsv(text)
        if (rows.length === 0) {
          message.error('CSV is empty')
          return false
        }
        // Header check
        const header = rows[0].map((c) => c.trim().toLowerCase())
        const codeCol = header.indexOf('emp_code')
        const bidCol = header.indexOf('biometric_id')
        if (codeCol < 0 || bidCol < 0) {
          message.error('CSV must have columns: emp_code, biometric_id (+ optional emp_name)')
          return false
        }
        const body = rows.slice(1)
          .filter((r) => r[codeCol]?.trim())
          .map((r) => ({
            emp_code: r[codeCol].trim(),
            biometric_id: (r[bidCol] ?? '').trim(),
          }))
        if (body.length === 0) {
          message.error('No valid rows in CSV')
          return false
        }
        bulkMut.mutate(body)
      } catch (e) {
        message.error(`Failed to read CSV: ${e}`)
      }
      return false // prevent default upload
    },
  }

  return (
    <Card
      style={{ marginTop: 16 }}
      title={
        <Space>
          <UserSwitchOutlined />
          <span>Employee Mapping</span>
        </Space>
      }
      extra={canEdit && (
        <Space wrap>
          <Button icon={<ThunderboltOutlined />} type="primary" ghost
            onClick={() => setQuickOpen(true)}>
            Quick Map
          </Button>
          <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
            Download Template
          </Button>
          <Upload {...uploadProps}>
            <Button type="primary" icon={<UploadOutlined />}
              loading={bulkMut.isPending}>
              Upload CSV
            </Button>
          </Upload>
        </Space>
      )}
    >
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Map your device's User IDs to employees"
        description={
          <div style={{ fontSize: 12 }}>
            <p style={{ marginTop: 0 }}>
              <b>One at a time:</b> Click <b>Quick Map</b> (top right) — pick
              an employee and type the biometric ID from the device. Or click
              the pencil <EditOutlined /> next to any row below.
            </p>
            <p style={{ marginBottom: 0 }}>
              <b>In bulk:</b> Click <b>Download Template</b> → fill the{' '}
              <Text code>biometric_id</Text> column in Excel → <b>Upload CSV</b>.
              Duplicate IDs (same number on two employees) are blocked.
            </p>
          </div>
        }
      />

      {/* Stats strip */}
      <div style={{
        display: 'grid', gap: 8, marginBottom: 12,
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
      }}>
        <StatTile value={stats.total} label="Total employees" />
        <StatTile value={stats.mapped} label="Mapped" tone="green" />
        <StatTile value={stats.unmapped} label="Not mapped" tone="amber" />
      </div>

      {/* Upload result report */}
      {uploadResult && (
        <Alert
          type={uploadResult.ok ? 'success' : 'error'}
          style={{ marginBottom: 12 }}
          message={
            uploadResult.ok
              ? `${uploadResult.updated} updated, ${uploadResult.skipped} unchanged`
              : `${uploadResult.errors} error(s) — nothing saved`
          }
          description={
            uploadResult.rows.length > 0 && (
              <div style={{ maxHeight: 200, overflow: 'auto', fontSize: 11 }}>
                {uploadResult.rows.slice(0, 50).map((r, i) => (
                  <div key={i} style={{
                    padding: '2px 0',
                    color: r.status === 'ERROR' ? '#cf1322' : undefined,
                  }}>
                    <Tag style={{
                      margin: 0, fontSize: 10,
                      color: r.status === 'ERROR' ? '#cf1322'
                        : r.status === 'UPDATED' ? '#52c41a' : undefined,
                    }}>{r.status}</Tag>{' '}
                    {r.emp_code} → {r.biometric_id || '(unset)'}
                    {r.reason && <span> · {r.reason}</span>}
                  </div>
                ))}
                {uploadResult.rows.length > 50 && (
                  <Text type="secondary">…and {uploadResult.rows.length - 50} more</Text>
                )}
              </div>
            )
          }
          closable
          onClose={() => setUploadResult(null)}
        />
      )}

      {/* Search */}
      <Input.Search
        allowClear
        placeholder="Search by emp code, name, or biometric ID"
        style={{ marginBottom: 12, maxWidth: 360 }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Current mapping table */}
      <Table
        rowKey="id"
        loading={isLoading}
        size="small"
        pagination={{ pageSize: 50, showSizeChanger: true }}
        columns={[
          { title: 'Emp Code', dataIndex: 'emp_code', key: 'code', width: 110,
            render: (s: string) => <Text code>{s}</Text> },
          { title: 'Name', dataIndex: 'emp_name', key: 'name' },
          { title: 'Biometric ID', dataIndex: 'biometric_id', key: 'bid', width: 140,
            render: (s: string | null) => s
              ? <Tag color="blue">{s}</Tag>
              : <Text type="secondary">—</Text> },
          ...(canEdit ? [{
            title: '', key: 'act', width: 50,
            render: (_: unknown, r: EmployeeMapping) => (
              <Button size="small" type="text" icon={<EditOutlined />}
                onClick={() => setEditing(r)} />
            ),
          }] : []),
        ] as never}
        dataSource={filtered}
      />

      {/* Inline edit modal (pencil per row) */}
      <SingleMapModal
        employee={editing}
        onClose={() => setEditing(null)}
        onSubmit={(emp_id, bid) =>
          singleMut.mutate({ employee_id: emp_id, biometric_id: bid })}
        submitting={singleMut.isPending}
      />

      {/* Quick Map modal (top-right button) */}
      <QuickMapModal
        open={quickOpen}
        employees={mappings ?? []}
        onClose={() => setQuickOpen(false)}
        onSubmit={(emp_id, bid) =>
          singleMut.mutate({ employee_id: emp_id, biometric_id: bid })}
        submitting={singleMut.isPending}
      />
    </Card>
  )
}


// ─── Single-employee mapping modals ───────────────────────────────────

function SingleMapModal({ employee, onClose, onSubmit, submitting }: {
  employee: EmployeeMapping | null
  onClose: () => void
  onSubmit: (employee_id: string, biometric_id: string) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  return (
    <Modal
      open={!!employee} onCancel={onClose} destroyOnClose
      title={employee ? `Map biometric ID — ${employee.emp_name}` : 'Map'}
      okText="Save mapping" confirmLoading={submitting} width={460}
      onOk={() => {
        if (!employee) return
        form.validateFields().then((v) => {
          onSubmit(employee.id, String(v.biometric_id).trim())
        })
      }}
    >
      {employee && (
        <Form form={form} layout="vertical" initialValues={{
          biometric_id: employee.biometric_id ?? '',
        }}>
          <Form.Item label="Employee">
            <Input value={`${employee.emp_code} — ${employee.emp_name}`} disabled />
          </Form.Item>
          <Form.Item name="biometric_id" label="Biometric ID (from device)"
            rules={[{ required: true, message: 'Enter the user ID shown on the device' }]}
            tooltip="The numeric User ID assigned when this person was enrolled on the device (e.g. 1, 2, 1001).">
            <Input placeholder="e.g. 1001" autoFocus />
          </Form.Item>
        </Form>
      )}
    </Modal>
  )
}


function QuickMapModal({ open, employees, onClose, onSubmit, submitting }: {
  open: boolean
  employees: EmployeeMapping[]
  onClose: () => void
  onSubmit: (employee_id: string, biometric_id: string) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  return (
    <Modal
      open={open} onCancel={onClose} destroyOnClose
      title="Quick Map — single employee"
      okText="Save mapping" confirmLoading={submitting} width={520}
      onOk={() => form.validateFields().then((v) => {
        onSubmit(v.employee_id, String(v.biometric_id).trim())
      })}
    >
      <Form form={form} layout="vertical">
        <Form.Item name="employee_id" label="Employee"
          rules={[{ required: true }]}>
          <Select
            showSearch placeholder="Search by name or emp code…"
            optionFilterProp="label"
            options={employees.map((e) => ({
              value: e.id,
              label: `${e.emp_code} — ${e.emp_name}${
                e.biometric_id ? ` · current: ${e.biometric_id}` : ''
              }`,
            }))}
          />
        </Form.Item>
        <Form.Item name="biometric_id" label="Biometric ID (from device)"
          rules={[{ required: true }]}
          tooltip="The User ID assigned when this person was enrolled on the device.">
          <Input placeholder="e.g. 1" />
        </Form.Item>
      </Form>
    </Modal>
  )
}


function StatTile({ value, label, tone = 'neutral' }: {
  value: number; label: string; tone?: 'neutral' | 'green' | 'amber'
}) {
  const color =
    tone === 'green' ? '#52c41a' :
    tone === 'amber' ? '#fa8c16' : '#1677ff'
  return (
    <div style={{
      background: '#fafafa', border: '1px solid #f0f0f0',
      borderRadius: 8, padding: '8px 12px',
    }}>
      <Title level={3} style={{ margin: 0, color }}>{value}</Title>
      <Text type="secondary" style={{ fontSize: 11 }}>{label}</Text>
    </div>
  )
}


// ─── CSV helpers ──────────────────────────────────────────────────────

function csvCell(s: string): string {
  if (/[,"\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

function parseCsv(text: string): string[][] {
  // Minimal CSV parser supporting quoted fields with embedded commas + quotes.
  const rows: string[][] = []
  let cur: string[] = []
  let field = ''
  let inQuotes = false
  let i = 0
  while (i < text.length) {
    const c = text[i]
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') {
        field += '"'; i += 2; continue
      }
      if (c === '"') { inQuotes = false; i++; continue }
      field += c; i++; continue
    }
    if (c === '"') { inQuotes = true; i++; continue }
    if (c === ',') { cur.push(field); field = ''; i++; continue }
    if (c === '\r') { i++; continue }
    if (c === '\n') { cur.push(field); rows.push(cur); cur = []; field = ''; i++; continue }
    field += c; i++
  }
  if (field !== '' || cur.length > 0) { cur.push(field); rows.push(cur) }
  return rows
}
